// SEQ-145 payment-claim summary: client-side mirror of the edge function's
// computeClaimSummary (supabase/functions/generate-progress-claim/claim-summary.ts).
//
// The app cannot import across into supabase/functions, so this file duplicates
// the engine math verbatim, exactly like variationClaimPreview.ts mirrors the
// edge function's variation math. A parity test (claimSummary.test.ts) imports
// BOTH modules and asserts identical output on every field, so the two copies
// cannot drift apart silently.
//
// Semantics (plan §3, decisions Q1/Q3/Q6):
//   A = CumP + PaidVar + V + HireClaimedToDate  (total claimed to date, no retention)
//   HireClaimedToDate = hire selected for THIS claim + hire attached to
//   EARLIER claims (SEQ-216: never hire that no claim selected, e.g. a pending row)
//   B = paidByBuilderIncGst / 1.1 + retention previously held on PAID claims
//   C = A - B                              (may exceed P + V when priors are unpaid)
//   R = C x retentionPct, clamped to the running retention ceiling headroom
//   G = (C - R + release) x 10%
//   T = C - R + release + G

export interface ClaimSummaryInput {
  cumulativeProgressToDate: number;
  progressThisClaim: number;
  paidVariationsTotal: number;
  variationsOutstanding: number;
  variationsThisMonth: number;
  installHireThisClaim?: number;
  /**
   * SEQ-216: hire CLAIMED to date = the hire rows selected for this claim
   * (installHireThisClaim) + the rows attached to earlier claims of the project
   * (sumPriorClaimedHire). This is A's hire component and already contains
   * this claim's own hire, so installHireThisClaim must NOT also be added to A.
   * It is NOT the sum of every billing row: a pending row no claim selected is
   * not claimed. Never source this from the Install & Hire tab's Running Total
   * card - that card accrues live to today and drifts daily.
   */
  hireClaimedToDate?: number;
  paidByBuilderIncGst: number;
  priorRetentionOnPaidClaims: number;
  retentionPct: number;
  remainingRetentionCapacity?: number;
  retentionReleaseAmount?: number;
}

export interface ClaimSummaryResult {
  cumP: number;
  P: number;
  paidVar: number;
  variationsOutstanding: number;
  variationsThisMonth: number;
  V: number;
  installHire: number;
  A: number;
  B: number;
  /**
   * Amendment 2 display split of B (deduction math unchanged, presentation
   * only): bReceipts = receipts / 1.1; retentionHeldToDate = retention held on
   * PAID claims (the same figure B adds back - NOT the project overview card,
   * which also counts retention recorded on unpaid claims).
   */
  bReceipts: number;
  retentionHeldToDate: number;
  C: number;
  R: number;
  release: number;
  G: number;
  /** C - R + release: the post-retention ex-GST payable base (stored as total_exc_gst). */
  totalExcGst: number;
  T: number;
}

const GST_RATE = 0.1;

function roundCurrency(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Strict period precedence for the B inputs (audit fix, 23 Jul 2026): a prior
 * claim is one from a period strictly BEFORE the claim being generated.
 * Excluding only the same period would let LATER claims' receipts/retention
 * leak into B when a back-dated claim is (re)generated, overstating B and
 * understating C.
 */
export function isPriorClaimPeriod(
  rowYear: number,
  rowMonth: number,
  claimYear: number,
  claimMonth: number,
): boolean {
  return rowYear < claimYear || (rowYear === claimYear && rowMonth < claimMonth);
}

export function computeClaimSummary(input: ClaimSummaryInput): ClaimSummaryResult {
  const cumP = roundCurrency(input.cumulativeProgressToDate);
  const P = roundCurrency(input.progressThisClaim);
  const paidVar = roundCurrency(input.paidVariationsTotal);
  const variationsOutstanding = roundCurrency(input.variationsOutstanding);
  const variationsThisMonth = roundCurrency(input.variationsThisMonth);
  const V = roundCurrency(variationsOutstanding + variationsThisMonth);
  const installHire = roundCurrency(input.installHireThisClaim ?? 0);

  const hireClaimedToDate = roundCurrency(input.hireClaimedToDate ?? 0);

  // A's hire component is hire claimed to date (SEQ-216), not this claim's
  // selected hire alone (the claimed-to-date figure already contains it).
  const A = roundCurrency(cumP + paidVar + V + hireClaimedToDate);
  const bReceipts = roundCurrency(input.paidByBuilderIncGst / (1 + GST_RATE));
  const retentionHeldToDate = roundCurrency(input.priorRetentionOnPaidClaims);
  const B = roundCurrency(bReceipts + retentionHeldToDate);
  const C = roundCurrency(A - B);

  const retentionBase = Math.max(0, C);
  const pct = Math.max(0, input.retentionPct);
  const capacity =
    input.remainingRetentionCapacity != null &&
      Number.isFinite(input.remainingRetentionCapacity)
      ? Math.max(0, input.remainingRetentionCapacity)
      : Number.POSITIVE_INFINITY;
  const rawRetention = roundCurrency(retentionBase * (pct / 100));
  const R = roundCurrency(Math.min(rawRetention, capacity));

  const release = roundCurrency(Math.max(0, input.retentionReleaseAmount ?? 0));
  const totalExcGst = roundCurrency(Math.max(0, retentionBase - R) + release);
  const G = roundCurrency(totalExcGst * GST_RATE);
  const T = roundCurrency(totalExcGst + G);

  return {
    cumP,
    P,
    paidVar,
    variationsOutstanding,
    variationsThisMonth,
    V,
    installHire,
    A,
    B,
    bReceipts,
    retentionHeldToDate,
    C,
    R,
    release,
    G,
    totalExcGst,
    T,
  };
}

export interface RetentionCeilingStatus {
  /**
   * All-claims retention held INCLUDING this claim's retention. Matches the
   * project overview "Retention Held" card and the ceiling clamp basis. This is
   * the "actual amount" a builder tracks - deliberately DIFFERENT from
   * ClaimSummaryResult.retentionHeldToDate, which is the paid-claims-only add-back
   * used inside B for the A-B-C payable math and must not change.
   */
  heldToDate: number;
  ceiling: number;
  /** max(0, ceiling - heldToDate). */
  remaining: number;
  /**
   * heldToDate exceeds the retention ceiling. Auto-generation clamps R so this should
   * only surface on legacy/manually-entered data; the report renders it as a
   * warning rather than silently hiding it.
   */
  overCeiling: boolean;
}

/**
 * The true retention-held-to-date figure for the Send Report + modal display.
 * accumulatedRetention = sum of every prior claim's retention (ALL claims, not
 * paid-only, excluding the claim being generated); thisClaimRetention = R.
 * Together they equal what the project overview card shows, so the report's
 * ceiling status reconciles with the app instead of understating held retention
 * whenever a prior claim is unpaid.
 */
export function retentionCeilingStatus(
  accumulatedRetention: number,
  thisClaimRetention: number,
  ceiling: number,
): RetentionCeilingStatus {
  const held = roundCurrency(
    Math.max(0, accumulatedRetention) + Math.max(0, thisClaimRetention),
  );
  const cap = roundCurrency(Math.max(0, ceiling));
  const remaining = roundCurrency(Math.max(0, cap - held));
  return {
    heldToDate: held,
    ceiling: cap,
    remaining,
    overCeiling: held > cap + 0.005,
  };
}

export interface HireBillingPeriodRow {
  billing_month?: number | null;
  billing_year?: number | null;
  amount?: number | string | null;
}

/**
 * Sum of billing rows with period <= the claim month. A back-dated claim must
 * not count later billing periods. SEQ-216: callers pass ONLY rows that a claim
 * actually selected; passing every row of the project is the bug this fixes.
 */
export function sumHireBilledToDate(
  rows: readonly HireBillingPeriodRow[],
  claimYear: number,
  claimMonth: number,
): number {
  const total = rows.reduce((sum, row) => {
    const y = Number(row.billing_year || 0);
    const m = Number(row.billing_month || 0);
    const within = y < claimYear || (y === claimYear && m <= claimMonth);
    return within ? sum + Number(row.amount || 0) : sum;
  }, 0);
  return roundCurrency(total);
}

export interface PriorReportHireInput {
  id?: string | null;
  claim_month?: number | string | null;
  claim_year?: number | string | null;
}

export interface AttachedHireRow extends HireBillingPeriodRow {
  id?: string | null;
  claim_report_id?: string | null;
  paid?: boolean | null;
}

export interface SumPriorClaimedHireOptions {
  /** The report being generated or regenerated, when one already exists. */
  currentReportId?: string | null;
  /** Row ids selected for THIS claim: they are counted as this claim's hire. */
  selectedIds?: ReadonlySet<string>;
}

/**
 * SEQ-216: hire already claimed by claims OTHER than the one being selected,
 * from the rows as they are now. A row counts when its billing period is <= the
 * claim month, it is not in this claim's selection, and one of these holds:
 *   1. it is attached (claim_report_id) to a report from a period strictly
 *      before the claim being generated;
 *   2. it is PAID and attached to the report being regenerated: paid rows are
 *      never claimable, are not released at save, and stay on that report;
 *   3. it is PAID and attached to nothing (claim deletion detaches paid rows
 *      too, but the builder paid them, so they were claimed).
 * Current amounts, not frozen per-report lists, are the source: a row can be
 * legitimately re-priced after it was claimed (a 100% variation discount,
 * SEQ-169) and the earlier claim already reversed it, and a frozen list can
 * itemise one row twice. Never counted: a pending row no claim owns, an
 * unpaid row of the report being regenerated (released at save unless it is
 * re-selected), and anything attached to a later report. The selectedIds
 * guard means a row can never count both here and in the claim's own hire.
 */
export function sumPriorClaimedHire(
  reports: readonly PriorReportHireInput[],
  rows: readonly AttachedHireRow[],
  claimYear: number,
  claimMonth: number,
  options: SumPriorClaimedHireOptions = {},
): number {
  const priorIds = new Set<string>();
  for (const report of reports) {
    if (
      report.id &&
      isPriorClaimPeriod(
        Number(report.claim_year),
        Number(report.claim_month),
        claimYear,
        claimMonth,
      )
    ) {
      priorIds.add(report.id);
    }
  }
  const currentReportId = options.currentReportId || null;
  const selectedIds = options.selectedIds;
  return sumHireBilledToDate(
    rows.filter((row) => {
      if (row.id && selectedIds?.has(row.id)) return false;
      const owner = row.claim_report_id || null;
      if (owner && priorIds.has(owner)) return true;
      if (!row.paid) return false;
      return owner === null || owner === currentReportId;
    }),
    claimYear,
    claimMonth,
  );
}

export interface SelectedHireRow {
  id?: string | null;
  amount?: number | string | null;
}

export interface HireClaimedToDateInput {
  /** The rows ticked / selected for the claim being generated. */
  selectedRows: readonly SelectedHireRow[];
  reports: readonly PriorReportHireInput[];
  /** Project rows that can be claimed elsewhere (see sumPriorClaimedHire). */
  rows: readonly AttachedHireRow[];
  claimYear: number;
  claimMonth: number;
  currentReportId?: string | null;
}

/**
 * SEQ-216: A's hire component, shared by the edge function and the modal
 * preview so the two cannot pick different rows: this claim's selected rows
 * plus everything claimed elsewhere. thisClaim equals installHireThisMonth.
 */
export function computeHireClaimedToDate(input: HireClaimedToDateInput): {
  thisClaim: number;
  priorClaimed: number;
  total: number;
} {
  const thisClaim = roundCurrency(
    input.selectedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
  );
  const selectedIds = new Set<string>();
  for (const row of input.selectedRows) {
    if (row.id) selectedIds.add(row.id);
  }
  const priorClaimed = sumPriorClaimedHire(
    input.reports,
    input.rows,
    input.claimYear,
    input.claimMonth,
    { currentReportId: input.currentReportId, selectedIds },
  );
  return {
    thisClaim,
    priorClaimed,
    total: roundCurrency(thisClaim + priorClaimed),
  };
}

/**
 * PostgREST .or() filter for the rows sumPriorClaimedHire can count: attached
 * to one of the owner reports (earlier reports + the one being regenerated),
 * or paid and attached to nothing. One builder for the edge function and the
 * modal so both read the same rows.
 */
export function buildHireClaimedElsewhereFilter(
  ownerReportIds: readonly string[],
): string {
  const ids = ownerReportIds.filter((id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
  return [
    ids.length > 0 ? `claim_report_id.in.(${ids.join(",")})` : null,
    "and(claim_report_id.is.null,paid.eq.true)",
  ].filter(Boolean).join(",");
}

/**
 * SEQ-216: the report ids whose attached rows can count as claimed elsewhere:
 * every report from a period strictly before the claim, plus the report being
 * regenerated. The existing-report lookup (by project, month, year) and the
 * reports list describe the same row, so they must agree: a transient failure
 * or a stale lookup would silently drop the regenerated claim's own paid rows
 * from A. Throws a fixed message when they disagree.
 */
export function resolveHireOwnerIds(input: {
  reports: readonly PriorReportHireInput[];
  claimYear: number;
  claimMonth: number;
  existingReportId: string | null;
}): string[] {
  const { reports, claimYear, claimMonth, existingReportId } = input;
  const samePeriod = reports.find(
    (r) =>
      Number(r.claim_year) === claimYear && Number(r.claim_month) === claimMonth,
  );
  if ((samePeriod?.id ?? null) !== existingReportId) {
    throw new Error("Could not load this claim. Please try again.");
  }
  const ids = reports
    .filter((r) =>
      isPriorClaimPeriod(
        Number(r.claim_year),
        Number(r.claim_month),
        claimYear,
        claimMonth,
      )
    )
    .map((r) => r.id)
    .filter((id): id is string => typeof id === "string");
  if (existingReportId) ids.push(existingReportId);
  return ids;
}

/**
 * True when a PostgREST page holds fewer rows than the server counted
 * (max_rows = 1000): an A built from it would silently omit claimed hire.
 */
export function isTruncatedPage(
  count: number | null | undefined,
  rows: readonly unknown[] | null | undefined,
): boolean {
  return typeof count === "number" && count > (rows?.length ?? 0);
}
